import React, { useState, useEffect } from "react";
import { Upload, Image } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const LogoManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isDefaultLogo, setIsDefaultLogo] = useState(false);

  useEffect(() => {
    api.setAiAgentId(activeAiAgentId);
    fetchCurrentLogo();
  }, [activeAiAgentId]);

  const fetchCurrentLogo = async (forceRefresh = false) => {
    try {
      const data = await api.getCurrentLogo();
      if (data && data.exists) {
        let url = data.logo_url;
        if (forceRefresh) {
          url += (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
        }

        setLogoPreview(url);
        setIsDefaultLogo(!!data.isDefault);
      } else {
        setLogoPreview(null);
        setIsDefaultLogo(false);
      }
    } catch (error) {
      console.error("Error fetching logo:", error);
      setLogoPreview(null);
    }
  };



  const handleLogoUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        showToast("File size must be less than 5MB", "error");
        return;
      }
      setLogo(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      showToast("Please select a valid image file", "error");
    }
  };

  const handleFileInput = (event) => {
    const file = event.target.files[0];
    if (file) handleLogoUpload(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoUpload(file);
  };

  const uploadLogo = async () => {
    if (logo) {
      try {
        setIsLoading(true);
        await api.uploadLogo(logo);
        showToast("Logo updated successfully!");
        setTimeout(async () => {
          await fetchCurrentLogo(true);
        }, 800);
        setLogo(null);
      } catch (error) {
        console.error("Error saving logo:", error);
        showToast("Failed to upload logo. Please try again.", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-6xl space-y-12">
        <PageHeader 
          title="Brand Identity"
          description="Orchestrate your brand's visual presence. Define the neural signature that represents your intelligence."
        />

        <div className="grid md:grid-cols-5 gap-8 animate-slideIn">
          {/* Current Logo Preview */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 sticky top-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Image size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Active Logo</h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    {isDefaultLogo ? "Default Asset" : "Custom Brand"}
                  </p>
                </div>
              </div>

              <div className="relative group">
                <div className="aspect-square bg-gray-50 rounded-[24px] border border-gray-100 p-6 flex items-center justify-center overflow-hidden transition-all duration-500 group-hover:shadow-inner">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Current logo"
                      className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default.png";
                      }}
                    />
                  ) : (
                    <div className="text-gray-300 flex flex-col items-center gap-2">
                      <Image size={48} />
                      <span className="text-xs font-medium">No Logo Set</span>
                    </div>
                  )}
                </div>
              </div>

              <p className="mt-6 text-xs text-center text-gray-400 leading-relaxed">
                This logo will be displayed in the chat widget header and companion apps.
              </p>
            </div>
          </div>

          {/* Upload Section */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 h-full">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                  <Upload size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Update Branding</h3>
                  <p className="text-[11px] font-medium text-gray-400">Upload a new identity for your agent</p>
                </div>
              </div>

              <div
                className={`
                  relative rounded-[24px] p-12 border-2 border-dashed transition-all duration-300 cursor-pointer text-center group
                  ${dragActive ? "border-blue-400 bg-blue-50/50" : "border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-gray-50"}
                `}
                onClick={() => document.getElementById("logo-input").click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:shadow-md">
                    <Upload className={`w-8 h-8 ${dragActive ? "text-blue-500" : "text-gray-400"}`} />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-1 leading-tight">
                    {dragActive ? "Drop Identity Here" : "Upload Brand Logo"}
                  </h4>
                  <p className="text-xs text-gray-400 mb-4 px-8">
                    Drag and drop or click to browse files
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {['PNG', 'JPG', 'SVG', 'WEBP'].map(ext => (
                      <span key={ext} className="px-2 py-1 bg-white rounded-md border border-gray-100 text-[10px] font-bold text-gray-400">{ext}</span>
                    ))}
                  </div>
                </div>
              </div>

              <input
                id="logo-input"
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
              />

              {logo && (
                <div className="mt-8 p-5 bg-white rounded-[24px] border border-blue-100 shadow-sm shadow-blue-50/50 animate-slideUp">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                      <Image className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {logo.name}
                      </p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                        {(logo.size / 1024 / 1024).toFixed(2)} MB • Ready
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={uploadLogo}
                    disabled={isLoading}
                    className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-all font-black text-sm shadow-xl shadow-gray-200"
                  >
                    {isLoading ? "Synchronizing..." : "Apply Changes"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LogoManagement;
